import { Static, Type } from '@sinclair/typebox';
import {
  ExtractedBusinessSchema,
  ExtractedPrestationSchema,
  ExtractedTeamMemberSchema,
  WebsitePreviewExtractedSchema,
} from './website_preview_extracted';
import { SourceKindSchema } from './website_discovery';

// Contrat des événements streamés par le Business Detection Pipeline
// (orchestrateur `detect$` backend → SSE → wizard d'onboarding).
// Union discriminée sur `kind` — chaque événement partiel porte juste le
// sous-ensemble pertinent ; `done` porte le payload complet et consolidé.
// Cf. docs/to_implement/business-detection-pipeline-plan.md §5 et §7.

// Fiche Google trouvée ET match jugé fiable (cf. règle §3.3 : pas de match
// confiant ⇒ cet événement n'est jamais émis, bascule silencieuse from-scratch).
export const DetectionPlaceFoundEventSchema = Type.Object({
  kind: Type.Literal('place_found'),
  business: ExtractedBusinessSchema,
  rating: Type.Optional(Type.Number()),
  reviews_count: Type.Optional(Type.Number()),
  website_uri: Type.Optional(Type.String()),
});

export const DetectionWebsiteFoundEventSchema = Type.Object({
  kind: Type.Literal('website_found'),
  url: Type.String(),
});

export const DetectionPlatformsFoundEventSchema = Type.Object({
  kind: Type.Literal('platforms_found'),
  platforms: Type.Array(
    Type.Object({
      kind: SourceKindSchema,
      url: Type.String(),
    }),
  ),
});

// Progress — émis au démarrage du scrape de chaque source (peut arriver plusieurs fois).
export const DetectionScrapingEventSchema = Type.Object({
  kind: Type.Literal('scraping'),
  source_url: Type.String(),
  page_count: Type.Optional(Type.Number()),
});

export const DetectionPrestationsFoundEventSchema = Type.Object({
  kind: Type.Literal('prestations_found'),
  prestations: Type.Array(ExtractedPrestationSchema),
});

export const DetectionTeamFoundEventSchema = Type.Object({
  kind: Type.Literal('team_found'),
  team: Type.Array(ExtractedTeamMemberSchema),
});

// Photos déjà ré-hébergées : full-keys S3 (préfixées ENVIRONMENT_VERSION), à signer
// via Thumbor côté backend avant affichage (même convention que le reste des images).
export const DetectionPhotosFoundEventSchema = Type.Object({
  kind: Type.Literal('photos_found'),
  photos: Type.Array(Type.String()),
});

/**
 * Une source dont la détection a tiré quelque chose, telle que l'écran 9 du tunnel la présente au
 * pro pour validation (« Votre fiche Google · Inès Nails, adresse, horaires, 12 avis » ; « Votre
 * site · ines-nails.fr, 3 pages » ; « Planity, 1 page »). `kind = google` = la fiche Places ; les
 * autres sont les pages scrapées avec succès. `key` identifie la source dans la revue (`google` ou
 * l'URL), et c'est ce que le pro peut écarter.
 */
export const DetectionSourceSummarySchema = Type.Object({
  key: Type.String(),
  kind: SourceKindSchema,
  url: Type.Optional(Type.String()),
  title: Type.Optional(Type.String()),
  /** Pages lues sur cette source (hors fiche Google). */
  page_count: Type.Optional(Type.Integer({ minimum: 0 })),
  /** Fiche Google seulement : note, nombre d'avis, adresse, horaires présents. */
  rating: Type.Optional(Type.Number()),
  reviews_count: Type.Optional(Type.Integer({ minimum: 0 })),
  address: Type.Optional(Type.String()),
  has_hours: Type.Optional(Type.Boolean()),
  photos_count: Type.Optional(Type.Integer({ minimum: 0 })),
  /**
   * Source retenue sur un signal unique (fiche trouvée par le numéro, sans nom ni ville qui
   * confirment) : l'écran 9 la montre décochée, le pro la coche s'il s'y reconnaît.
   */
  weak: Type.Optional(Type.Boolean()),
});
export type DetectionSourceSummaryObject = Static<typeof DetectionSourceSummarySchema>;

/**
 * Dimensions (px) lues dans l'en-tête des images au ré-hébergement, indexées par clé S3 complète.
 * Le gate qualité de la preview (`qualifyExtracted`) refuse toute photo dont il ne connaît pas les
 * dimensions : sans elles, un enrichissement depuis le snapshot ne poserait jamais de photo réelle.
 */
export const DetectionImageDimensionsSchema = Type.Record(
  Type.String(),
  Type.Object({ width: Type.Number(), height: Type.Number() }),
);
export type DetectionImageDimensionsObject = Static<typeof DetectionImageDimensionsSchema>;

// Fin de détection. `no_match: true` ⇔ aucun match fiable (règle §3.3) : le wizard
// bascule silencieusement en from-scratch, `extracted` est alors absent. Sinon
// `extracted` = payload complet consolidé (photos déjà sur S3). `sources` = d'où ça vient
// (écran 9 du tunnel v4) ; absent sur les pipelines qui ne le renseignent pas.
export const DetectionDoneEventSchema = Type.Object({
  kind: Type.Literal('done'),
  no_match: Type.Boolean(),
  extracted: Type.Optional(WebsitePreviewExtractedSchema),
  sources: Type.Optional(Type.Array(DetectionSourceSummarySchema)),
  /**
   * Données de la seule fiche Google quand elle est `weak` (retenue sur le numéro, sans nom ni ville
   * qui confirment) : tenues À PART de `extracted`, qui ne contient alors que les pages lues. Elles
   * ne sont fusionnées qu'à la revue de l'écran 9, si le pro garde la fiche.
   */
  place_only: Type.Optional(WebsitePreviewExtractedSchema),
  /** Dimensions des photos ré-hébergées (`extracted` et `place_only`), pour le gate qualité de la preview. */
  image_dimensions: Type.Optional(DetectionImageDimensionsSchema),
  /** Clés S3 des photos venues de la fiche Google (cf. `DetectionSnapshotSchema.google_photo_keys`). */
  google_photo_keys: Type.Optional(Type.Array(Type.String())),
});

// Best-effort : une étape en échec émet `error` et n'interrompt pas forcément la suite.
export const DetectionErrorEventSchema = Type.Object({
  kind: Type.Literal('error'),
  step: Type.String(),
  message: Type.String(),
});

// Snapshot durable persisté en JSONB sur `professional.detection_snapshot`.
// Cache (pas de re-run au refresh) ET trace process-agnostique de la détection :
// découplé du pipeline qui l'a produit, il reste exploitable même si le process
// change. `extracted` garde les clés S3 BRUTES (re-signées Thumbor à l'émission).
// Validé via `Value.Check` à la relecture (cf. pattern LeadQualification).
export const DetectionSnapshotSchema = Type.Object({
  // v2 : invalide les snapshots v1 (horaires Google parsés en anglais AM/PM →
  // 02:00-07:00 / jours fermés à tort). `Value.Check` rejette les v1 → re-détection.
  // v3 : invalide les v2 dont le `business.name` venait du scrape plateforme (ex.
  // Planity « … II ») au lieu du nom GMB autoritatif — corrigé dans le merge.
  // v4 : ajout des `variants` tarifaires sur les prestations (catalogue complet,
  // Lot B2). Les v3 sont invalidés → re-détection propre (cache jetable).
  // v5 : ajout de `business.activity_labels` (métiers détectés) → auto-rattachement
  // des activités du référentiel (Lot B5). Les v4 sont invalidés → re-détection.
  version: Type.Literal(5),
  input: Type.Object({
    business_name: Type.String(),
    city: Type.String(),
  }),
  // ISO 8601 — horodatage de production du snapshot (fraîcheur / invalidation future).
  detected_at: Type.String(),
  no_match: Type.Boolean(),
  extracted: Type.Optional(WebsitePreviewExtractedSchema),
  /** Fiche Google `weak` tenue à part (cf. `DetectionDoneEventSchema.place_only`). */
  place_only: Type.Optional(WebsitePreviewExtractedSchema),
  /**
   * Tunnel v4 (écran 9) : détection lancée EN ARRIÈRE-PLAN à l'inscription (job BullMQ). `pending`
   * = job en file ou en cours (`detected_at` = heure de mise en file, `no_match` provisoire) ;
   * `done` ou absent = résultat final. Un `pending` plus vieux que `DETECTION_PENDING_TIMEOUT_MS`
   * est traité comme un échec silencieux (mesuré le 28/10 : 2 min à 2 min 30 sur un salon avec site,
   * Planity et fiche Google ; l'écran 9 propose de continuer sans attendre bien avant).
   */
  status: Type.Optional(Type.Union([Type.Literal('pending'), Type.Literal('done')])),
  /** D'où vient `extracted` (écran 9) — absent sur les détections du wizard v1. */
  sources: Type.Optional(Type.Array(DetectionSourceSummarySchema)),
  /**
   * Dimensions des photos ré-hébergées (cf. `DetectionDoneEventSchema.image_dimensions`) : relues par
   * l'enrichissement de la démo après l'inscription (gate qualité). Absentes sur les snapshots
   * antérieurs ⇒ leurs photos ne passent pas le gate (jamais de photo non vérifiée sur la démo).
   */
  image_dimensions: Type.Optional(DetectionImageDimensionsSchema),
  /**
   * Clés S3 (après ré-hébergement) des photos qui viennent de la fiche Google (fiable ou `weak`),
   * hors photos aussi trouvées sur le site. Permet de retirer exactement ces photos quand le pro
   * écarte la fiche, ou de ne garder qu'elles quand il écarte les pages (le compte `photos_count`
   * de la source est pris AVANT rehost et dédoublonnage, il ne dit pas lesquelles ont survécu).
   * Absent sur les snapshots antérieurs : la sélection ne sait alors pas les isoler.
   */
  google_photo_keys: Type.Optional(Type.Array(Type.String())),
  /**
   * Revue du pro à l'écran 9 : horodatage et clés des sources écartées (`google` ou l'URL). Posée
   * une fois ; l'écran 9 ne se représente plus ensuite. Une source écartée n'alimente ni le
   * catalogue (si toutes les sources de pages le sont) ni le profil (fiche Google écartée).
   */
  review: Type.Optional(
    Type.Object({
      reviewed_at: Type.String(),
      rejected_keys: Type.Array(Type.String()),
    }),
  ),
});
export type DetectionSnapshotObject = Static<typeof DetectionSnapshotSchema>;

/** Au-delà, une détection `pending` est considérée perdue : l'écran 9 est sauté. */
export const DETECTION_PENDING_TIMEOUT_MS = 180_000;
/** Clé de la fiche Google dans `sources` / `review.rejected_keys`. */
export const DETECTION_SOURCE_GOOGLE_KEY = 'google';

export const DetectionEventSchema = Type.Union([
  DetectionPlaceFoundEventSchema,
  DetectionWebsiteFoundEventSchema,
  DetectionPlatformsFoundEventSchema,
  DetectionScrapingEventSchema,
  DetectionPrestationsFoundEventSchema,
  DetectionTeamFoundEventSchema,
  DetectionPhotosFoundEventSchema,
  DetectionDoneEventSchema,
  DetectionErrorEventSchema,
]);
export type DetectionEventObject = Static<typeof DetectionEventSchema>;
export type DetectionEventKind = DetectionEventObject['kind'];
